import React, { useState } from "react";
import {
  hasFieldError,
  getFieldErrorMessage,
  getInputStyles,
  getLabelStyles,
  getLabelText,
} from "../utils/validation";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Button,
  Paper,
  Box,
  Image,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconTrash,
  IconPlus,
  IconPhoto,
} from "@tabler/icons-react";
import CustomRichTextEditor from "../../common/RichTextEditor";
import WebsiteGeneratorPhotoGallery from "./WebsiteGeneratorPhotoGallery";

const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";

interface TestimonialCard {
  id: number;
  name: string;
  title: string;
  text: string;
  image?: {
    id?: string | number;
    url?: string;
    src?: string;
    filename?: string;
    [key: string]: any;
  };
}

interface TestimonialsContent {
  title?: string;
  description?: string;
  body?: string;
  cards?: TestimonialCard[];
}

interface WebsiteGeneratorTestimonialsProps {
  onBack: () => void;
  testimonialsContent?: TestimonialsContent;
  onContentChange: (content: TestimonialsContent) => void;
  missingFields?: string[];
  requiredValidation?: { [field: string]: boolean };
}

const WebsiteGeneratorTestimonials: React.FC<
  WebsiteGeneratorTestimonialsProps
> = ({
  onBack,
  testimonialsContent,
  onContentChange,
  missingFields = [],
  requiredValidation = {},
}) => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [isPhotoGalleryOpen, setIsPhotoGalleryOpen] = useState(false);
  const [currentCardForImage, setCurrentCardForImage] = useState<number | null>(
    null,
  );

  const cards =
    testimonialsContent?.cards && testimonialsContent.cards.length > 0
      ? testimonialsContent.cards
      : [{ id: 1, name: "", title: "", text: "", image: undefined }];

  const handleTitleChange = (value: string) => {
    if (value.length > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        title: "Title cannot exceed 100 characters",
      }));
      return;
    }
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.title;
      return next;
    });
    onContentChange({
      ...(testimonialsContent || {}),
      title: value,
    });
  };

  const handleDescriptionChange = (value: string) => {
    if (value.length > 200) {
      setValidationErrors((prev) => ({
        ...prev,
        description: "Description cannot exceed 200 characters",
      }));
      return;
    }
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.description;
      return next;
    });
    onContentChange({
      ...(testimonialsContent || {}),
      description: value,
    });
  };

  const handleBodyChange = (value: string) => {
    const wordCount = value
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
    if (wordCount > 250) {
      setValidationErrors((prev) => ({
        ...prev,
        body: "Body cannot exceed 250 words",
      }));
      return;
    }
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.body;
      return next;
    });
    onContentChange({
      ...(testimonialsContent || {}),
      body: value,
    });
  };

  const handleCardFieldChange = (
    cardId: number,
    field: keyof TestimonialCard,
    value: string,
  ) => {
    const updatedCards = cards.map((card) =>
      card.id === cardId ? { ...card, [field]: value } : card,
    );
    onContentChange({
      ...(testimonialsContent || {}),
      cards: updatedCards,
    });
  };

  const handleCardImageSelect = (cardId: number) => {
    setCurrentCardForImage(cardId);
    setIsPhotoGalleryOpen(true);
  };

  const handleImageConfirm = () => {
    // This is called when the user confirms the selection
    // The actual image selection is handled by onImageSelect
  };

  const handleAddCard = () => {
    if (cards.length >= 5) {
      setValidationErrors((prev) => ({
        ...prev,
        cards: "Maximum 5 cards allowed",
      }));
      return;
    }
    const newId =
      cards.length > 0 ? Math.max(...cards.map((c) => c.id)) + 1 : 1;
    const updatedCards = [
      ...cards,
      { id: newId, name: "", title: "", text: "", image: undefined },
    ];
    onContentChange({
      ...(testimonialsContent || {}),
      cards: updatedCards,
    });
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.cards;
      return next;
    });
  };

  const handleRemoveCard = (cardId: number) => {
    if (cards.length <= 1) {
      setValidationErrors((prev) => ({
        ...prev,
        cards: "Minimum 1 card required",
      }));
      return;
    }
    const updatedCards = cards.filter((card) => card.id !== cardId);
    onContentChange({
      ...(testimonialsContent || {}),
      cards: updatedCards,
    });
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.cards;
      return next;
    });
  };

  const getWordCount = (text?: string) => {
    if (!text) return 0;
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  // Check if testimonials cards field has error (when enabled but empty)
  // The API uses "testimonials.testimonials" but component uses "cards"
  // The validation dict should map testimonials.testimonials -> cards
  const hasCardsError =
    hasFieldError("cards", requiredValidation, cards) ||
    missingFields.includes("testimonials.testimonials") ||
    missingFields.includes("testimonials.cards");

  // Check if title field has error
  const hasTitleError =
    hasFieldError("title", requiredValidation, testimonialsContent?.title) ||
    missingFields.includes("testimonials.header");

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Testimonials
        </Text>
      </Group>

      {/* Title */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles(
            "title",
            requiredValidation,
            testimonialsContent?.title,
          )}
        >
          {getLabelText(
            "Title",
            "title",
            requiredValidation,
            testimonialsContent?.title,
          )}
        </Text>
        <TextInput
          placeholder="Testimonials"
          value={testimonialsContent?.title || ""}
          onChange={(event) => handleTitleChange(event.currentTarget.value)}
          error={getFieldErrorMessage(
            "title",
            requiredValidation,
            validationErrors,
            testimonialsContent?.title,
          )}
          maxLength={100}
          styles={getInputStyles(
            "title",
            requiredValidation,
            missingFields,
            "testimonials.header",
            testimonialsContent?.title,
          )}
        />
      </Box>

      {/* Description */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Description
        </Text>
        <TextInput
          placeholder="Enter description..."
          value={testimonialsContent?.description || ""}
          onChange={(event) =>
            handleDescriptionChange(event.currentTarget.value)
          }
          error={validationErrors.description}
          maxLength={200}
        />
      </Box>

      {/* Body */}
      <Box>
        <CustomRichTextEditor
          label="Body"
          placeholder="Enter body text..."
          value={testimonialsContent?.body || ""}
          onChange={(htmlValue) => handleBodyChange(htmlValue)}
          minHeight={180}
        />
        {validationErrors.body && (
          <Text size="xs" c="red" mt="xs">
            {validationErrors.body}
          </Text>
        )}
        <Text size="xs" c="dimmed" mt="xs">
          {getWordCount(testimonialsContent?.body)} words
        </Text>
      </Box>

      {/* Cards */}
      <Box>
        <Group justify="space-between" mb="xs">
          <Text
            size="sm"
            fw={500}
            style={hasCardsError ? { color: "red" } : undefined}
          >
            Cards ({cards.length}/5) {hasCardsError && "*"}
          </Text>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddCard}
            disabled={cards.length >= 5}
          >
            Add Card
          </Button>
        </Group>
        {(validationErrors.cards || hasCardsError) && (
          <Text size="xs" c="red" mb="xs">
            {validationErrors.cards ||
              (hasCardsError
                ? "At least one testimonial card is required"
                : "")}
          </Text>
        )}

        <Stack gap="md">
          {cards.map((card) => (
            <Paper
              key={card.id}
              p="md"
              style={{
                border: `1px solid ${COLOR_BORDER_LIGHT}`,
                borderRadius: "8px",
                backgroundColor: COLOR_BACKGROUND_LIGHT,
              }}
            >
              <Group justify="space-between" mb="md">
                <Text size="sm" fw={500}>
                  Card {cards.indexOf(card) + 1}
                </Text>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => handleRemoveCard(card.id)}
                  disabled={cards.length <= 1}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>

              {/* Image */}
              <Box mb="sm">
                <Text size="sm" fw={500} mb="xs">
                  Image
                </Text>
                {card.image ? (
                  <Box>
                    <Image
                      src={card.image.url || card.image.src}
                      alt="Testimonial"
                      height={100}
                      width={100}
                      fit="cover"
                      style={{ borderRadius: "8px", marginBottom: "8px" }}
                    />
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconPhoto size={14} />}
                      onClick={() => handleCardImageSelect(card.id)}
                    >
                      Change Image
                    </Button>
                  </Box>
                ) : (
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPhoto size={14} />}
                    onClick={() => handleCardImageSelect(card.id)}
                  >
                    Select Image
                  </Button>
                )}
              </Box>

              {/* Name */}
              <Box mb="sm">
                <Text size="sm" fw={500} mb="xs">
                  Name
                </Text>
                <TextInput
                  placeholder="Enter name..."
                  value={card.name || ""}
                  onChange={(event) =>
                    handleCardFieldChange(
                      card.id,
                      "name",
                      event.currentTarget.value,
                    )
                  }
                  maxLength={100}
                />
              </Box>

              {/* Title */}
              <Box mb="sm">
                <Text size="sm" fw={500} mb="xs">
                  Title
                </Text>
                <TextInput
                  placeholder="Enter title..."
                  value={card.title || ""}
                  onChange={(event) =>
                    handleCardFieldChange(
                      card.id,
                      "title",
                      event.currentTarget.value,
                    )
                  }
                  maxLength={100}
                />
              </Box>

              {/* Text */}
              <Box>
                <CustomRichTextEditor
                  label="Text"
                  placeholder="Enter testimonial text..."
                  value={card.text || ""}
                  onChange={(htmlValue) =>
                    handleCardFieldChange(card.id, "text", htmlValue)
                  }
                  minHeight={120}
                />
              </Box>
            </Paper>
          ))}
        </Stack>
      </Box>

      {/* Photo Gallery Modal */}
      <WebsiteGeneratorPhotoGallery
        isOpen={isPhotoGalleryOpen}
        onClose={() => {
          setIsPhotoGalleryOpen(false);
          setCurrentCardForImage(null);
        }}
        images={[]}
        selectedImages={
          currentCardForImage !== null
            ? cards
                .filter((c) => c.id === currentCardForImage && c.image)
                .map((c) => c.image!)
            : []
        }
        onImageSelect={(selectedImages) => {
          // Handle single image selection
          if (currentCardForImage !== null && selectedImages.length > 0) {
            const updatedCards = cards.map((card) =>
              card.id === currentCardForImage
                ? { ...card, image: selectedImages[0] }
                : card,
            );
            onContentChange({
              ...(testimonialsContent || {}),
              cards: updatedCards,
            });
          }
        }}
        onConfirmSelection={handleImageConfirm}
      />
    </Stack>
  );
};

export default WebsiteGeneratorTestimonials;
